import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseEntries } from '../../utils/prescriptionUtils';

const PrescriptionEntries = ({ value = '', onChange, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const [entries, setEntries] = useState(() => parseEntries(value));
  const [input, setInput] = useState('');
  const [isAdding, setIsAdding] = useState(() => parseEntries(value).length === 0);
  const [editingIndex, setEditingIndex] = useState(null);

  const joinedEntries = entries.join('\n\n');

  useEffect(() => {
    if (value !== joinedEntries) {
      const parsed = parseEntries(value);
      setEntries(parsed);
      setInput('');
      setEditingIndex(null);
    }
  }, [value, joinedEntries]);

  const saveCurrentEntry = (andExit = false) => {
    const text = input.trim();
    if (!text) return;

    let nextEntries;
    if (editingIndex !== null) {
      nextEntries = entries.map((entry, index) => (index === editingIndex ? text : entry));
    } else {
      nextEntries = [...entries, text];
    }

    setEntries(nextEntries);
    onChange(nextEntries.join('\n\n'));
    setInput('');
    setEditingIndex(null);
    setIsAdding(!andExit);
  };

  const handleEdit = (index) => {
    setInput(entries[index] || '');
    setEditingIndex(index);
    setIsAdding(true);
  };

  const handleDelete = (index) => {
    const nextEntries = entries.filter((_, i) => i !== index);
    setEntries(nextEntries);
    onChange(nextEntries.join('\n\n'));
    if (editingIndex === index) {
      setInput('');
      setEditingIndex(null);
      setIsAdding(nextEntries.length === 0);
    }
  };

  const handleCancelEdit = () => {
    setInput('');
    setEditingIndex(null);
    setIsAdding(entries.length === 0);
  };

  const cardStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '12px 16px'
  };

  const textStyle = {
    flex: 1,
    fontSize: '16px',
    lineHeight: '1.5',
    color: '#111827',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    textAlign: isRTL ? 'right' : 'left'
  };

  const actionGroupStyle = {
    display: 'flex',
    gap: '8px',
    flexShrink: 0
  };

  const iconButtonStyle = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: color === 'danger' ? '#FEE2E2' : '#E0F2FE',
    color: color === 'danger' ? '#DC2626' : '#0284C7',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s'
  });

  const textareaStyle = {
    width: '100%',
    minHeight: '100px',
    padding: '12px 16px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: '#FFFFFF',
    textAlign: isRTL ? 'right' : 'left'
  };

  const buttonRowStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: isRTL ? 'flex-start' : 'flex-end'
  };

  const primaryButtonStyle = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: '#22C55E',
    color: '#FFFFFF',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s'
  };

  const secondaryButtonStyle = {
    ...primaryButtonStyle,
    background: '#3B82F6'
  };

  const ghostButtonStyle = {
    ...primaryButtonStyle,
    background: 'transparent',
    color: '#6B7280',
    border: '1px solid #D1D5DB'
  };

  const addButtonStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px dashed #D1D5DB',
    background: '#FFFFFF',
    color: '#22C55E',
    fontSize: '14px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {entries.map((entry, index) => {
        if (index === editingIndex) return null;
        return (
          <div key={index} style={cardStyle}>
            <div style={textStyle}>{entry}</div>
            <div style={actionGroupStyle}>
              <button
                type="button"
                title={t('common.edit', { defaultValue: 'Edit' })}
                onClick={() => handleEdit(index)}
                disabled={disabled || editingIndex !== null}
                style={iconButtonStyle('info')}
              >
                <i className="fas fa-pencil-alt" style={{ fontSize: '13px' }}></i>
              </button>
              <button
                type="button"
                title={t('common.delete', { defaultValue: 'Delete' })}
                onClick={() => handleDelete(index)}
                disabled={disabled || editingIndex !== null}
                style={iconButtonStyle('danger')}
              >
                <i className="fas fa-trash-alt" style={{ fontSize: '13px' }}></i>
              </button>
            </div>
          </div>
        );
      })}

      {isAdding && (
        <>
          <textarea
            style={textareaStyle}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('receptionOutpatients.checkout.sections.prescription.placeholder', { defaultValue: 'Enter prescription...' })}
            disabled={disabled}
          />
          <div style={buttonRowStyle}>
            {editingIndex !== null && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={disabled}
                style={ghostButtonStyle}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
            )}
            <button
              type="button"
              onClick={() => saveCurrentEntry(false)}
              disabled={disabled || !input.trim()}
              style={primaryButtonStyle}
            >
              {t('common.save', { defaultValue: 'Save' })}
            </button>
            <button
              type="button"
              onClick={() => saveCurrentEntry(true)}
              disabled={disabled || !input.trim()}
              style={secondaryButtonStyle}
            >
              {t('common.saveAndExit', { defaultValue: 'Save and exit' })}
            </button>
          </div>
        </>
      )}

      {!isAdding && (
        <button
          type="button"
          onClick={() => {
            setIsAdding(true);
            setEditingIndex(null);
            setInput('');
          }}
          disabled={disabled}
          style={addButtonStyle}
        >
          <i className="fas fa-plus" style={{ marginInlineEnd: '8px' }}></i>
          {t('common.add', { defaultValue: 'Add prescription' })}
        </button>
      )}
    </div>
  );
};

export default PrescriptionEntries;
